import * as React from 'react';
import { Icon, Label, LabelProps, Tooltip } from '@patternfly/react-core';
import { ExclamationTriangleIcon, GlobeIcon, PlayIcon, PowerOffIcon } from '@patternfly/react-icons';
import { InstructorName } from '@app/components/InstructorName';
import { Course } from '@app/model/Course';
import { KLASS_DECISION, KLASS_MODALITY, KLASS_STATUS, KLASS_TYPE, PAYMENT_MODE, REGION, REQUEST_STATUS } from '@app/model/Enums';
import { SKILL_LEVEL } from '@app/model/Enums';
import { Instructor } from '@app/model/Instructor';
import { Klass, PrivateKlass, isDeliveryAddressUnreadable } from '@app/model/Klass';
import { getSkillLevel } from '@app/model/Skill';

import { ClassStatusDisplayConfig } from '@app/utils/config';

/**
 * Enum values that title-casing gets wrong, because they are acronyms or names rather than words:
 * NIIT would read "Niit" and TSS "Tss". Matched whole, so nothing here can catch a longer value by
 * accident.
 */
const SPELT_OUT: Record<string, string> = {
  GUARANTEED_TO_RUN: 'GTR',
  NIIT: 'NIIT',
  NON_NIIT: 'Non-NIIT',
  RED_HAT: 'Red Hat',
  TSS: 'TSS',
  RHLS_PREMIUM: 'RHLS Premium',
  // The operating units. Every one of them is an acronym, so title-casing would read "Emea".
  EMEA: 'EMEA',
  NAM: 'NAM',
  APAC: 'APAC',
  LATAM: 'LATAM'
};

/** An enum value as a reader should see it: "GUARANTEED_TO_RUN" -> "GTR", "IN_PERSON" -> "In Person". */
export const formatStatusText = (status: string): string => {
  const spelt = SPELT_OUT[status];
  if (spelt) return spelt;

  return status
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());
};

// 1. Status Label Component
export const ClassStatusLabel: React.FC<{ status?: KLASS_STATUS | string | null }> = ({ status }) => {
  if (!status) return <React.Fragment>N/A</React.Fragment>;

  const statusKey = status as string;
  const labelText = formatStatusText(statusKey);
  
  // Pull configuration, fallback to DEFAULT if the status is missing from the dictionary
  const config = ClassStatusDisplayConfig[statusKey] || ClassStatusDisplayConfig.DEFAULT;

  // Determine specific icons
  let icon: React.ReactNode = undefined;
  if (statusKey === KLASS_STATUS.CONFIRMED || statusKey === KLASS_STATUS.GUARANTEED_TO_RUN) {
    icon = <PlayIcon />;
  } else if (statusKey === KLASS_STATUS.CANCELLED) {
    icon = <PowerOffIcon />;
  }

  return (
    <Label 
      color={config!.pfColor} 
      variant={config!.pfVariant} 
      icon={icon}
      // Depending on your PF6 setup, you can also inject status="info" or status="success" here if explicitly needed, 
      // but the `color` prop handles standard styling perfectly.
    >
      {labelText}
    </Label>
  );
};

// 2. Type Label Component
export const ClassTypeLabel: React.FC<{ type?: KLASS_TYPE | string | null }> = ({ type }) => {
  switch (type) {
    case KLASS_TYPE.OPEN_ENROLMENT: return <Label color="blue">OE</Label>;
    case KLASS_TYPE.PRIVATE: return <Label color="green">Private</Label>;
    case KLASS_TYPE.AD_HOC: return <Label color="teal">Ad Hoc</Label>;
    default: return <React.Fragment>{type != null ? String(type) : 'N/A'}</React.Fragment>;
  }
};

// 3. Modality Label Component
export const ClassModalityLabel: React.FC<{ modality?: KLASS_MODALITY | string | null }> = ({ modality }) => {
  switch (modality) {
    case KLASS_MODALITY.VIRTUAL: return <Label color="orange" variant='outline'>Virtual</Label>;
    case KLASS_MODALITY.IN_PERSON: return <Label color="green" variant='outline'>In Person</Label>;
    case KLASS_MODALITY.RHLS_PREMIUM: return <Label color="purple" variant='outline'>RHLS Premium</Label>;
    default: return <React.Fragment>{modality != null ? String(modality) : 'N/A'}</React.Fragment>;
  }
};

// 3b. Region Label Component

/**
 * A colour per operating unit, so the same unit reads the same wherever it turns up.
 *
 * Four colours that say nothing: which unit sold a class is not good news or bad, so red and
 * yellow are left out - a red LATAM label beside a status column would read as an alarm on a
 * class with nothing wrong with it. The globe icon is what tells Region apart from the Modality
 * and Type labels it sits beside, rather than the colours being kept clear of theirs.
 */
const REGION_COLORS: Record<string, LabelProps['color']> = {
  [REGION.EMEA]: 'blue',
  [REGION.NAM]: 'teal',
  [REGION.APAC]: 'purple',
  [REGION.LATAM]: 'orange'
};

/**
 * The Red Hat operating unit a class was sold out of.
 *
 * Carries the explanation with it, because the field reads as the country the class is delivered in
 * and is not that - see REGION.
 *
 * Nothing at all when the class names no unit: the Classes Outlook report leaves it blank for RHLS
 * classes among others, so a class with no region is normal rather than incomplete, and an "N/A"
 * on each of them would only be noise down a column and beside a title.
 */
export const ClassRegionLabel: React.FC<{ region?: REGION | string | null }> = ({ region }) => {
  if (!region) return null;

  return (
    <Tooltip content="The Red Hat operating unit the class was sold out of, which is a different thing from the country it is delivered in.">
      <Label color={REGION_COLORS[region as string] ?? 'grey'} variant="outline" icon={<GlobeIcon />}>
        {formatStatusText(region as string)}
      </Label>
    </Tooltip>
  );
};

// 4. Payment Mode Label Component
export const PaymentModeLabel: React.FC<{ paymentMode?: PAYMENT_MODE | string | null }> = ({ paymentMode }) => {
  switch (paymentMode) {
    case PAYMENT_MODE.PURCHASE_ORDER: return <Label color="blue" variant='outline'>Purchase Order</Label>;
    case PAYMENT_MODE.TRAINING_UNITS: return <Label color="green" variant='outline'>Training Units</Label>;
    default: return <React.Fragment>{paymentMode != null ? String(paymentMode) : 'N/A'}</React.Fragment>;
  }
};

// 5. Decision Label Component
export const ClassDecisionLabel: React.FC<{ decision?: KLASS_DECISION | string | null }> = ({ decision }) => {
  if (!decision) return <React.Fragment>N/A</React.Fragment>;

  const decisionKey = decision as string;
  const labelText = formatStatusText(decisionKey);

  switch (decisionKey) {
    case KLASS_DECISION.PENDING: 
      return <Label color="yellow">{labelText}</Label>;
    case KLASS_DECISION.CONFIRMED: 
      return <Label color="green">{labelText}</Label>;
    case KLASS_DECISION.DELIVERED: 
      return <Label color="green" variant="outline">{labelText}</Label>;
    case KLASS_DECISION.CANCELLED: 
      return <Label color="red">{labelText}</Label>;
    case KLASS_DECISION.PUBLISHED: 
      return <Label color="blue">{labelText}</Label>;
    case KLASS_DECISION.UNPUBLISHED: 
      return <Label color="grey" variant="outline">{labelText}</Label>;
    case KLASS_DECISION.NON_NIIT_DELIVERY: 
      return <Label color="purple">{labelText}</Label>;
    case KLASS_DECISION.COMMUNICATED:
      return <Label color="teal">{labelText}</Label>;
    default:
      return <Label color="grey">{labelText}</Label>;
  }
};

/**
 * 5b. Where a request has got to.
 *
 * Shared by the three places one turns up: a pipeline request's own status, and an ad hoc class's
 * requestStatus and tssApproval. The colours are the same as the class statuses they rhyme with -
 * green for approved, red for declined - so a reader does not have to learn a second scheme.
 */
export const RequestStatusLabel: React.FC<{ status?: REQUEST_STATUS | string | null }> = ({ status }) => {
  if (!status) return <React.Fragment>N/A</React.Fragment>;

  const labelText = formatStatusText(status as string);

  switch (status) {
    case REQUEST_STATUS.APPROVED: return <Label color="green">{labelText}</Label>;
    case REQUEST_STATUS.SUBMMITTED: return <Label color="blue">{labelText}</Label>;
    case REQUEST_STATUS.PENDING: return <Label color="yellow">{labelText}</Label>;
    case REQUEST_STATUS.DECLINED: return <Label color="red">{labelText}</Label>;
    case REQUEST_STATUS.CANCELLED: return <Label color="grey" variant="outline" icon={<PowerOffIcon />}>{labelText}</Label>;
    default: return <Label color="grey">{labelText}</Label>;
  }
};

// 6. Instructors ----------------------------------------------------------------

/**
 * One of a class's instructor lists, as the tables and the detail page show it: the names
 * in order, or a red N/A when nobody is assigned. A class can be delivered by more than
 * one instructor, so every one of the three fields is a list.
 *
 * Given the course the list is being read against, each name also carries whether that
 * instructor may deliver it - see InstructorName. Every caller that knows its course passes
 * it; History does not, deliberately, since whether a certification is current says nothing
 * about a class that has already been delivered. Each name links to the instructor's own page
 * either way: looking somebody up is worth doing whether or not their certification is in question.
 */
export const InstructorNames: React.FC<{ instructors?: Instructor[] | null; course?: Course | null }> =
  ({ instructors, course }) => {
    const named = (instructors ?? []).filter(inst => inst?.name);
    if (!named.length) return <Label status="danger">N/A</Label>;

    return (
      <React.Fragment>
        {named.map((inst, index) => (
          <React.Fragment key={inst.id ?? inst.name}>
            {index > 0 && ', '}
            <InstructorName instructor={inst} course={course} />
          </React.Fragment>
        ))}
      </React.Fragment>
    );
  };

/**
 * The Instructor Level column: what each instructor delivering the class is recorded at for the
 * class's own course, and Unskilled where nothing is recorded - no skill row for the course, or
 * one with no level on it.
 *
 * Reads the instructors who are actually delivering, which is the final line-up once decided,
 * else the allocation, else whoever the source system named - resolveInstructors, the same answer
 * the calendar and its filters use. So the column follows the rightmost of the three instructor
 * columns that has anybody in it, and the tooltip names them where more than one is delivering.
 */
export const InstructorLevels: React.FC<{ instructors?: Instructor[] | null; course?: Course | null }> =
  ({ instructors, course }) => {
    const levels = (instructors ?? [])
      .filter(inst => inst?.name)
      .map(inst => ({ name: inst.name, level: getSkillLevel(inst, course) }))
      // An instructor whose skills never arrived, or a request with no course, answers nothing
      // rather than answering Unskilled - see getSkillLevel.
      .filter((read): read is { name: string; level: SKILL_LEVEL } => Boolean(read.level));

    if (!levels.length) return <React.Fragment>N/A</React.Fragment>;

    const text = levels.map(read => formatStatusText(read.level)).join(', ');
    if (levels.length === 1) return <React.Fragment>{text}</React.Fragment>;

    return (
      <Tooltip content={
        <div style={{ textAlign: 'left' }}>
          {levels.map(read => <div key={read.name}>{read.name}: {formatStatusText(read.level)}</div>)}
        </div>
      }>
        <span className="tooltipped-text">{text}</span>
      </Tooltip>
    );
  };

// 7. Location -------------------------------------------------------------------

/**
 * Only private classes carry a delivery address, and therefore only they can carry one the importer
 * failed to parse. Every other type is delivered at a facility - an ad hoc class included, since it
 * is an open enrolment class once approved.
 *
 * The two modalities are excluded because a class delivered online has nowhere to deliver to: it may
 * still carry an address off the sheet, but an unreadable one is not worth anybody's attention.
 */
const hasDeliveryAddress = (klass: Klass): boolean =>
  klass.modality !== KLASS_MODALITY.VIRTUAL
  && klass.modality !== KLASS_MODALITY.RHLS_PREMIUM
  && klass.type === KLASS_TYPE.PRIVATE;

/** The class's own city and country, for a class with no facility to name. Empty when neither is set. */
const cityAndCountry = (klass: Klass): string =>
  [klass.city, klass.country?.name].filter(Boolean).join(', ');

/**
 * Where a class is delivered, as plain text. Used to sort and to export the Location
 * column, and as the tooltip shown when the cell is too narrow for its content.
 * ClassLocation adds the unreadable-address warning icon on top of this.
 */
export const getKlassLocationText = (klass: Klass): string => {
  if (klass.modality === KLASS_MODALITY.VIRTUAL) return "Virtual Training";
  if (klass.modality === KLASS_MODALITY.RHLS_PREMIUM) return "RHLS Premium";

  if (klass.type !== KLASS_TYPE.PRIVATE) {
    return klass.facility?.name || cityAndCountry(klass) || "No location set";
  }

  const privateKlass = klass as PrivateKlass;
  const city = privateKlass.deliveryAddress?.city;

  // Safely handles if country is an object with a name property or just a flat string
  const rawCountry = privateKlass.deliveryAddress?.country as any;
  const countryName = typeof rawCountry === 'string' ? rawCountry : rawCountry?.name;

  if (city && countryName) return `${city}, ${countryName}`;
  if (!city && countryName) return String(countryName);
  // Not "the text is present" - that is now true of every imported private class, and this column
  // would read "Address unreadable" for all of them. See isDeliveryAddressUnreadable.
  if (isDeliveryAddressUnreadable(klass)) return "Address unreadable";
  return cityAndCountry(klass) || "No location set";
};

/** The Location cell of a classes table: the text above, flagged when the importer could not read
 * the address it was sent into an address at all. */
export const ClassLocation: React.FC<{ klass: Klass }> = ({ klass }) => {
  const text = getKlassLocationText(klass);
  if (!hasDeliveryAddress(klass) || !isDeliveryAddressUnreadable(klass)) {
    return <React.Fragment>{text}</React.Fragment>;
  }

  return (
    <React.Fragment>
      {text}
      <Tooltip content="The address the sheet sent could not be read into an address - see As received on the class, and the automation messages. Please fill it in by hand.">
        <Icon status="warning" style={{ marginLeft: '6px', verticalAlign: 'text-bottom' }}>
          <ExclamationTriangleIcon />
        </Icon>
      </Tooltip>
    </React.Fragment>
  );
};
